/**
 *  @class
 *  @function ImageTextOverlay
 */

if (!customElements.get('image-with-text-overlay')) {
  // Fail-open timeout for the theme editor when GSAP never becomes available.
  const EDITOR_REVEAL_FALLBACK_MS = 1200;

  class ImageTextOverlay extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
      this.animationsInitialized = false;
      this.editorRevealTimer = null;
      this.resizeHandler = null;
      this.blockSelectHandler = null;
      this.animationsReadyListener = null;
    }
    isDesignMode() {
      return typeof Shopify !== 'undefined' && Shopify.designMode;
    }
    animationsWillRunOnViewport() {
      try {
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
        if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) return false;
        if (navigator && navigator.connection && navigator.connection.saveData) return false;
      } catch (e) {}
      return true;
    }
    revealContent() {
      this.querySelectorAll('.image-with-text-overlay--heading, .image-with-text-overlay--content .rte p').forEach((el) => {
        el.style.visibility = 'visible';
      });
      this.querySelectorAll('.image-with-text-overlay--content .subheading, .image-with-text-overlay--content .button, .image-with-text-overlay--content .inline-badge').forEach((el) => {
        el.style.opacity = '1';
      });
    }
    connectedCallback() {
      const section = this;
      const designMode = this.isDesignMode();

      const initIfReady = () => {
        const animationsReady = document.documentElement.classList.contains('animations-ready');
        if (!(typeof gsap !== 'undefined' && (animationsReady || designMode))) {
          return false;
        }

        this.animationsInitialized = true;
        this.prepareAnimations();

        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }

        // Add resize handler to refresh SplitText on viewport changes
        let resizeTimer;
        let lastWidth = window.innerWidth;

        this.resizeHandler = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            const currentWidth = window.innerWidth;
            // Only refresh if viewport actually changed size (not just scroll)
            if (Math.abs(currentWidth - lastWidth) > 50) {
              lastWidth = currentWidth;

              if (this.splittext && this.tl) {
                // Kill existing timeline
                this.tl.kill();

                // Revert SplitText to original state
                this.splittext.revert();

                // Re-run animations with new viewport size
                this.prepareAnimations();
              }
            }
          }, 250);
        };

        window.addEventListener('resize', this.resizeHandler);
        return true;
      };

      if (!initIfReady()) {
        // Mobile skips GSAP in theme.liquid; show copy immediately instead of waiting forever.
        if (!this.animationsWillRunOnViewport()) {
          this.revealContent();
        } else {
          this.animationsReadyListener = () => initIfReady();
          document.addEventListener('theme:animations:ready', this.animationsReadyListener, { once: true });
        }

        // In the theme editor, fail open if GSAP never becomes available.
        if (designMode) {
          this.editorRevealTimer = window.setTimeout(() => {
            if (!this.animationsInitialized) {
              this.revealContent();
            }
          }, EDITOR_REVEAL_FALLBACK_MS);
        }
      }

      if (designMode) {
        this.blockSelectHandler = () => {
          if (this.tl && typeof this.tl.restart === 'function') {
            this.tl.restart();
          } else {
            this.revealContent();
          }
        };
        this.addEventListener('shopify:block:select', this.blockSelectHandler);
      }
    }
    disconnectedCallback() {
      if (this.tl && typeof this.tl.kill === 'function') {
        this.tl.kill();
      }
      if (this.splittext && typeof this.splittext.revert === 'function') {
        this.splittext.revert();
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      if (this.editorRevealTimer) {
        window.clearTimeout(this.editorRevealTimer);
        this.editorRevealTimer = null;
      }
      if (this.animationsReadyListener) {
        document.removeEventListener('theme:animations:ready', this.animationsReadyListener);
      }
      if (this.blockSelectHandler) {
        this.removeEventListener('shopify:block:select', this.blockSelectHandler);
      }

      this.animationsInitialized = false;
    }
    prepareAnimations() {
      let section = this,
        button_offset = 0,
        property = (gsap.getProperty('html', '--header-height') + gsap.getProperty('html', '--header-offset')) + 'px',
        designMode = this.isDesignMode();

      const reveal = this.revealContent.bind(this);

      // Fail open: don't leave text hidden if SplitText isn't available.
      if (typeof SplitText === 'undefined') {
        reveal();
        return;
      }

      section.tl = designMode
        ? gsap.timeline({ paused: true })
        : gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: 'top center'
            }
          });

      const fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
      fontsReady.then(function () {
        try {
          section.splittext = new SplitText(section.querySelectorAll('.image-with-text-overlay--heading, p:not(.subheading)'), {
            type: 'lines, words',
            linesClass: 'line-child'
          });
        } catch (e) {
          reveal();
          return;
        }

        if (section.querySelector('.inline-badge')) {
          section.tl
            .fromTo(section.querySelector('.inline-badge'), {
              opacity: 0
            }, {
              duration: 0.75,
              opacity: 1
            }, 0);

          button_offset += 0.5;
        }
        if (section.querySelector('.subheading')) {
          section.tl
            .fromTo(section.querySelector('.subheading'), {
              opacity: 0
            }, {
              duration: 0.75,
              opacity: 0.6
            }, 0);

          button_offset += 0.5;
        }
        if (section.querySelector('.image-with-text-overlay--heading')) {
          let h3_duration = 0.8 + ((section.querySelectorAll('.image-with-text-overlay--heading .line-child div').length - 1) * 0.08);
          section.tl
            .set(section.querySelector('.image-with-text-overlay--heading'), {
              visibility: 'visible'
            }, 0)
            .from(section.querySelectorAll('.image-with-text-overlay--heading .line-child div'), {
              duration: h3_duration,
              yPercent: '100',
              stagger: 0.08
            }, 0);
          button_offset += h3_duration;
        }
        if (section.querySelector('.rte p')) {
          let p_duration = 0.8 + ((section.querySelectorAll('.rte p .line-child div').length - 1) * 0.02);
          section.tl
            .set(section.querySelectorAll('.rte p'), {
              visibility: 'visible'
            }, 0)
            .from(section.querySelectorAll('.rte p .line-child div'), {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0);
          button_offset += p_duration;
        }
        if (section.querySelectorAll('.button')) {
          let i = 1;
          section.querySelectorAll('.button').forEach((item) => {
            section.tl.fromTo(item, {
              autoAlpha: 0
            }, {
              duration: 0.5,
              autoAlpha: 1
            }, ((button_offset * 0.4) + (i - 1) * 0.1));
            i++;
          });
        }

        if (designMode && section.tl) {
          section.tl.restart();
        }

      }).catch(function () {
        reveal();
      });

      if (!designMode && section.querySelector('.thb-parallax-image')) {
        gsap.fromTo(section.querySelectorAll('.thb-parallax-image'), {
          y: '-8%'
        }, {
          y: '8%',
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            scrub: 1,
            start: () => `top bottom`,
            end: () => `bottom top+=${property}`,
            onUpdate: () => {
              property = (gsap.getProperty('html', '--header-height') + gsap.getProperty('html', '--header-offset')) + 'px';
            }
          }
        });
      }
    }
  }
  customElements.define('image-with-text-overlay', ImageTextOverlay);
}